// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import './JsonEditor.scss';

interface FieldMetadata {
  type?: 'enum' | 'number' | 'string' | 'boolean';
  enumOptions?: Array<{ value: any; label: string }>;
  description?: string;
  displayLabel?: string;
}

interface JsonEditorProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
  label?: string;
  error?: string;
  showRawJson?: boolean;
  onChangeDetected?: (hasChanges: boolean) => void;
  fieldMetadata?: Record<string, FieldMetadata>;
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter JSON data...',
  disabled = false,
  rows = 6,
  className = '',
  label,
  error,
  showRawJson = false,
  onChangeDetected,
  fieldMetadata = {}
}) => {
  const [jsonFields, setJsonFields] = useState<Record<string, any>>({});
  const [jsonParseError, setJsonParseError] = useState('');
  const [isRawMode, setIsRawMode] = useState(showRawJson);
  const [rawValue, setRawValue] = useState(value || '');
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());
  const [originalValue, setOriginalValue] = useState(value || '');

  // Generate user-friendly display label for a field key
  const getDisplayLabel = (key: string): string => {
    const metadata = fieldMetadata[key];
    if (metadata?.displayLabel) {
      return metadata.displayLabel;
    }

    // Handle reward rank keys specially (e.g., "rewards.1" -> "🥇 1st Place Rewards")
    const rewardRankMatch = key.match(/rewards\.(\d+)$/);
    if (rewardRankMatch) {
      const rank = parseInt(rewardRankMatch[1]);
      const rankEmojis = { 1: '🥇', 2: '🥈', 3: '🥉', 4: '🏅' };
      const rankNames = { 1: '1st Place', 2: '2nd Place', 3: '3rd Place', 4: '4th Place' };
      return `${rankEmojis[rank as keyof typeof rankEmojis] || '🏅'} ${rankNames[rank as keyof typeof rankNames] || `${rank}th Place`} Rewards`;
    }

    // Handle reward type/amount keys (e.g., "rewards.1.0.type" -> "Currency Type")
    if (key.includes('rewards.') && key.endsWith('.type')) {
      return 'Currency Type';
    }
    if (key.includes('rewards.') && key.endsWith('.amount')) {
      return 'Reward Amount';
    }

    // Handle fee cost keys
    if (key.includes('fee_cost.type')) {
      return 'Entry Fee Currency';
    }
    if (key.includes('fee_cost.amount')) {
      return 'Entry Fee Amount';
    }

    // Handle arena mode
    if (key.endsWith('.mode')) {
      return 'Game Mode';
    }

    // Default: use the last part of the key
    const parts = key.split('.');
    return parts[parts.length - 1];
  };

  // Parse JSON into individual fields for editing (flattened for nested objects)
  const parseJsonToFields = (jsonString: string | undefined): Record<string, any> => {
    try {
      if (!jsonString || !jsonString.trim()) return {};
      const parsed = JSON.parse(jsonString);
      return flattenObject(parsed);
    } catch (error) {
      setJsonParseError('Invalid JSON format');
      return {};
    }
  };

  // Unflatten dot notation back to nested objects
  const unflattenObject = (flattened: Record<string, any>): any => {
    const result: any = {};
    
    for (const key in flattened) {
      if (flattened.hasOwnProperty(key)) {
        const keys = key.split('.');
        let current = result;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = flattened[key];
      }
    }
    
    return result;
  };

  // Reconstruct JSON from form fields
  const reconstructJsonFromFields = (fields: Record<string, any>): string => {
    try {
      setJsonParseError('');
      const unflattened = unflattenObject(fields);
      return JSON.stringify(unflattened, null, 2);
    } catch (error) {
      setJsonParseError('Error reconstructing JSON');
      return '';
    }
  };

  // Flatten nested objects into dot notation for editing
  const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
    const flattened: Record<string, any> = {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        
        if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          // Recursively flatten nested objects
          Object.assign(flattened, flattenObject(obj[key], newKey));
        } else {
          // Keep primitive values and arrays as-is
          flattened[newKey] = obj[key];
        }
      }
    }
    
    return flattened;
  };

  // Add a new field to the JSON
  const addJsonField = () => {
    const newKey = `field_${Object.keys(jsonFields).length + 1}`;
    setJsonFields(prev => ({
      ...prev,
      [newKey]: ''
    }));
  };

  // Add a nested field example
  const addNestedExample = () => {
    const exampleFields = {
      'user': '',
      'user.profile': '',
      'user.profile.name': 'John Doe',
      'user.profile.age': 30,
      'user.settings': '',
      'user.settings.theme': 'dark',
      'user.settings.notifications': true,
      'game': '',
      'game.stats': '',
      'game.stats.level': 5,
      'game.stats.score': 1250
    };
    
    setJsonFields(prev => ({
      ...prev,
      ...exampleFields
    }));
  };

  // Add a nested field to an existing object
  const addNestedField = (parentPath: string) => {
    const newKey = `${parentPath}.newField`;
    updateJsonField(newKey, '');
  };

  // Remove a field from the JSON
  const removeJsonField = (key: string) => {
    setJsonFields(prev => {
      const newFields = { ...prev };
      delete newFields[key];
      return newFields;
    });
  };

  // Update a field value (supports nested paths like "coinRewards.win")
  const updateJsonField = (path: string, value: any) => {
    setJsonFields(prev => {
      const newFields = { ...prev };
      const keys = path.split('.');
      
      if (keys.length === 1) {
        newFields[path] = value;
      } else {
        // Handle nested object updates
        let current = newFields;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      }
      
      return newFields;
    });
  };

  // Update field key (rename)
  const updateJsonFieldKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    
    setJsonFields(prev => {
      const newFields = { ...prev };
      const value = newFields[oldKey];
      delete newFields[oldKey];
      newFields[newKey] = value;
      return newFields;
    });
  };

  // Determine input type for a field value
  const getInputType = (value: any): string => {
    if (typeof value === 'boolean') return 'checkbox';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) return 'datetime-local';
    return 'text';
  };

  // Convert value for input display
  const getInputValue = (value: any, type: string): string | boolean => {
    if (type === 'checkbox') return Boolean(value);
    if (type === 'datetime-local' && typeof value === 'string') {
      return value.substring(0, 16); // Convert ISO string to datetime-local format
    }
    return String(value);
  };

  // Convert input value back to appropriate type
  const convertInputValue = (value: string | boolean, originalValue: any): any => {
    if (typeof originalValue === 'boolean') return Boolean(value);
    if (typeof originalValue === 'number') return Number(value);
    if (typeof originalValue === 'string' && originalValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return String(value) + 'Z'; // Add timezone back
    }
    return String(value);
  };

  // Initialize fields when value changes
  useEffect(() => {
    if (!isRawMode) {
      setJsonFields(parseJsonToFields(value));
    }
    setRawValue(value || '');
    setOriginalValue(value || '');
  }, [value, isRawMode]);

  // Check for changes and notify parent
  useEffect(() => {
    if (onChangeDetected) {
      const currentValue = isRawMode ? rawValue : reconstructJsonFromFields(jsonFields);
      const hasChanges = currentValue !== originalValue;
      onChangeDetected(hasChanges);
    }
  }, [jsonFields, rawValue, originalValue, isRawMode, onChangeDetected]);

  // Update parent when fields change
  useEffect(() => {
    if (!isRawMode && Object.keys(jsonFields).length > 0) {
      const reconstructed = reconstructJsonFromFields(jsonFields);
      if (reconstructed !== (value || '')) {
        onChange(reconstructed);
      }
    }
  }, [jsonFields, isRawMode, onChange, value]);

  // Handle raw mode changes
  const handleRawChange = (newValue: string) => {
    setRawValue(newValue);
    onChange(newValue);
  };

  // Toggle between raw and form mode
  const toggleMode = () => {
    if (isRawMode) {
      // Switching to form mode - parse current raw value
      const fields = parseJsonToFields(rawValue);
      setJsonFields(fields);
    } else {
      // Switching to raw mode - reconstruct from fields
      const reconstructed = reconstructJsonFromFields(jsonFields);
      setRawValue(reconstructed);
      onChange(reconstructed);
    }
    setIsRawMode(!isRawMode);
  };

  // Toggle collapse state for a path
  const toggleCollapse = (path: string) => {
    setCollapsedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Check if a path should be visible (not hidden by collapsed parent)
  const isPathVisible = (path: string): boolean => {
    const pathParts = path.split('.');
    for (let i = 1; i < pathParts.length; i++) {
      const parentPath = pathParts.slice(0, i).join('.');
      if (collapsedPaths.has(parentPath)) {
        return false;
      }
    }
    return true;
  };

  // Check if a path has children
  const hasChildren = (path: string): boolean => {
    return Object.keys(jsonFields).some(key => {
      // Check if this key is a direct child of the path
      if (key.startsWith(path + '.') && key !== path) {
        const remainingPath = key.substring(path.length + 1);
        // Only count as direct child if there are no more dots (not a grandchild)
        return !remainingPath.includes('.');
      }
      return false;
    });
  };

  // Get all parent paths that should exist as collapsible nodes
  const getParentPaths = (): Set<string> => {
    const parents = new Set<string>();
    const allKeys = Object.keys(jsonFields);
    
    for (const key of allKeys) {
      const parts = key.split('.');
      // Create parent paths for nested keys
      for (let i = 1; i < parts.length; i++) {
        const parentPath = parts.slice(0, i).join('.');
        parents.add(parentPath);
      }
    }
    
    return parents;
  };

  // Get organized tree structure for rendering
  const getTreeStructure = () => {
    const tree: Array<{key: string, value: any, level: number, hasChildren: boolean, isVisible: boolean, isParent: boolean}> = [];
    const parentPaths = Array.from(getParentPaths());
    const fieldKeys = Object.keys(jsonFields);
    const allKeysArray = [...fieldKeys, ...parentPaths];
    const uniqueKeys = Array.from(new Set(allKeysArray));
    const sortedKeys = uniqueKeys.sort();
    
    for (const key of sortedKeys) {
      const level = key.split('.').length - 1;
      const hasChildNodes = hasChildren(key);
      const isVisible = isPathVisible(key);
      const isParentNode = parentPaths.includes(key) && !jsonFields.hasOwnProperty(key);
      
      tree.push({
        key,
        value: isParentNode ? `` : jsonFields[key],
        level,
        hasChildren: hasChildNodes,
        isVisible,
        isParent: isParentNode
      });
    }
    
    return tree;
  };

  return (
    <div className={`json-editor ${className}`} style={{padding: '2rem'}}>
      
      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {jsonParseError && !isRawMode && (
        <div className="alert alert-warning">
          <strong>JSON Parse Error:</strong> {jsonParseError}
        </div>
      )}


      {isRawMode ? (
        <div className="raw-mode-container">
          <div className="json-fields-header">
            {label && <label className="form-label">{label}</label>}
            <div className="header-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mode-toggle-btn"
                onClick={toggleMode}
                disabled={disabled}
                title="Switch to tree view mode"
              >
                🌳 Tree
              </button>
            </div>
          </div>
          <textarea
            value={rawValue || ''}
            onChange={(e) => handleRawChange(e.target.value)}
            className="form-control json-textarea"
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            style={{ fontFamily: 'monospace', fontSize: '14px' }}
          />
        </div>
      ) : (
        <div className="json-fields-container">
          <div className="json-fields-header">
            {label && <label className="form-label">{label}</label>}
            <div className="header-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addJsonField}
                disabled={disabled}
              >
                + Add Field
              </button>
            </div>
          </div>
          
          {Object.keys(jsonFields).length === 0 ? (
            <div className="no-fields-message">
              <p>No fields found. Click "Add Field" to start editing.</p>
            </div>
          ) : (
            <div className="json-fields-list json-tree-view">
              {getTreeStructure().map((item) => {
                if (!item.isVisible) return null;
                
                const inputType = getInputType(item.value);
                const inputValue = getInputValue(item.value, inputType);
                const isCollapsed = collapsedPaths.has(item.key);
                
                return (
                  <div 
                    key={item.key} 
                    className={`json-field-row tree-node level-${item.level} ${item.isParent ? 'parent-node' : ''}`}
                    style={{ marginLeft: `${item.level * 20}px` }}
                  >
                    <div className="field-key">
                      <div className="key-container">
                        {item.hasChildren && (
                          <button
                            type="button"
                            className={`collapse-toggle ${isCollapsed ? 'collapsed' : 'expanded'}`}
                            onClick={() => toggleCollapse(item.key)}
                            disabled={disabled}
                            title={isCollapsed ? 'Expand' : 'Collapse'}
                          >
                            {isCollapsed ? '▶' : '▼'}
                          </button>
                        )}
                        <input
                          readOnly
                          type="text"
                          value={getDisplayLabel(item.key)}
                          className={`form-control key-input ${item.isParent ? 'parent-key' : ''}`}
                          disabled={disabled}
                          title={`${getDisplayLabel(item.key)} (${item.key})`}
                        />
                      </div>
                    </div>
                    <div className="field-value" style={{maxWidth: "50%"}}>
                      {item.isParent ? (
                        <span className="parent-indicator">{`{...}`}</span>
                      ) : (() => {
                        const metadata = fieldMetadata[item.key];
                        
                        if (metadata?.type === 'enum' && metadata.enumOptions) {
                          return (
                            <select
                              value={item.value}
                              onChange={(e) => {
                                const selectedOption = metadata.enumOptions?.find(opt => opt.value.toString() === e.target.value);
                                const convertedValue = selectedOption ? selectedOption.value : e.target.value;
                                updateJsonField(item.key, convertedValue);
                              }}
                              className="form-control enum-select"
                              disabled={disabled}
                              title={metadata.description}
                            >
                              <option value="">Select...</option>
                              {metadata.enumOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          );
                        } else if (inputType === 'checkbox') {
                          return (
                            <div className="form-check">
                              <input
                                type="checkbox"
                                checked={Boolean(inputValue)}
                                onChange={(e) => updateJsonField(item.key, e.target.checked)}
                                className="form-check-input"
                                disabled={disabled}
                              />
                              <label className="form-check-label">
                                {Boolean(inputValue) ? 'ON' : 'OFF'}
                              </label>
                            </div>
                          );
                        } else {
                          return (
                            <input
                              type={inputType}
                              value={inputValue as string}
                              style={{marginRight: "10px"}}
                              onChange={(e) => {
                                const convertedValue = convertInputValue(e.target.value, item.value);
                                updateJsonField(item.key, convertedValue);
                              }}
                              className="form-control"
                              placeholder="Field value"
                              disabled={disabled}
                              title={metadata?.description}
                            />
                          );
                        }
                      })()}
                    </div>
                    <div className="field-actions">

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JsonEditor;
